import classNames from 'classnames'
import { formatISO, parseISO } from 'date-fns'
import { useMemo, type ReactNode } from 'react'
import { Size, Spacing } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { noop } from '@opallabs/lambda'
import {
  Avatar,
  Button,
  ButtonIconPosition,
  ButtonSize,
  DotLoader,
  Icon,
  SidebarTabs,
} from '@opallabs/lapidary'
import {
  ClickToEditTitleInput,
  Field,
  type DateField,
  type FieldDefinition,
} from '@opallabs/satellite'
import { parseCalendarDate } from '../../monthGrid'
import {
  indexItemsById,
  resolveItemColor,
  type ResolveChosenColor,
} from '../../resolveItemColor'
import { CalendarSizes } from '../../sizes'
import {
  type CalendarItem,
  type OnCalendarItemEdit,
  type ResolveDisplay,
  type ResolveImageUrl,
} from '../../types'
import { type CalendarPanelFormatters } from '../formatters'
import {
  buildItemDates,
  buildItemFields,
  buildItemLabelSets,
  buildMomentContent,
  calendarKindGlyph,
  stepCycle,
  type CalendarFieldMark,
  type CalendarItemField,
  type CalendarItemLabelSet,
} from '../panelData'
import { CalendarContentRow } from '../rows'
import * as styles from '../styles'
import {
  type CalendarPanelStep,
  type CalendarPanelTab,
} from '../useCalendarPanel'

export type ItemPanelProps = Readonly<{
  /** The panel to go back to, when the item was opened from one. */
  back?: CalendarPanelStep
  /**
   * Whether the reader may change the item. False draws every field to be read
   * and nothing else, which is what a view-only reader sees.
   *
   * @default true
   */
  canEdit?: boolean
  /**
   * Ids the previous and next buttons walk through, wrapping at both ends. The
   * caller owns the order, because only it knows what list the item came from.
   */
  cycleIds: readonly string[]
  formatters: CalendarPanelFormatters
  item: CalendarItem
  /** Everything in view, used to name the item's parent moment and inherit its color. */
  items: readonly CalendarItem[]
  onBack: (step: CalendarPanelStep) => void
  onClose: () => void
  /**
   * Saves one field of the item. Without it the panel has nowhere to send a
   * change, so it draws everything to be read.
   */
  onEdit?: OnCalendarItemEdit
  /** Asks the host to open the item where it really lives. */
  onNavigate: (item: CalendarItem) => void
  /** Opens a piece of the moment's content, one step deeper than the moment. */
  onOpenChild: (id: string) => void
  onStep: (id: string) => void
  /**
   * Told which tab a person picked. The panel is taken down whenever something
   * opened inside it is on show, so only the caller can hold on to the answer.
   */
  onTabChange: (tab: CalendarPanelTab) => void
  /** Items not in view that the item's parent moment can still be found in. */
  referenceItems?: readonly CalendarItem[]
  /**
   * The channel mark the grid draws on the same item's chip. The panel asks
   * for the `field` variant, drawn at the size of a field's value icon.
   */
  renderChannelIcon?: (item: CalendarItem, variant?: 'field') => ReactNode
  /** The workflow mark the grid draws on the same item's chip. */
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
  /** Matches the grid, so the content rows wear the colours of their chips. */
  resolveChosenColor?: ResolveChosenColor
  resolveDisplay?: ResolveDisplay
  resolveImageUrl?: ResolveImageUrl
  /** The tab to show. A moment opens on its settings without one. */
  tab?: CalendarPanelTab
}>

/**
 * The nearest step on the way back that a person can still be sent to. A filter
 * can take a moment away while its content stays in view, and a way back to
 * something nobody can see is no way back at all.
 */
const reachableBack = (
  step: CalendarPanelStep | undefined,
  itemsById: ReadonlyMap<string, CalendarItem>
): CalendarPanelStep | undefined =>
  step === undefined || step.type === 'day' || itemsById.has(step.id)
    ? step
    : reachableBack(step.back, itemsById)

/** The days a date field may offer, and which edit picking one raises. */
type EditableDateBounds = Readonly<{
  /**
   * The only days the picker offers. Every rule the panel keeps bounds one
   * side of a date and never both, so days the other way are always offered.
   */
  allowedDates?: { start: Date } | { end: Date }
  field: 'endDate' | 'startDate'
}>

/** A day the calendar carries, as a `Date` the picker can bound itself by. */
const calendarDay = (
  isoDate: string | undefined,
  field: string,
  id: string
): Date | null =>
  isoDate === undefined ? null : parseCalendarDate(isoDate, { field, id })

/** One item in full: where it sits, what it belongs to, and how to open it. */
export const ItemPanel: React.FC<ItemPanelProps> = ({
  back,
  canEdit = true,
  cycleIds,
  formatters,
  item,
  items,
  onBack,
  onClose,
  onEdit,
  onNavigate,
  onOpenChild,
  onStep,
  onTabChange,
  referenceItems,
  renderChannelIcon,
  renderStatusIcon,
  resolveChosenColor,
  resolveDisplay,
  resolveImageUrl,
  tab,
}) => {
  const theme = useTheme()
  const itemsById = useMemo(() => indexItemsById(items), [items])
  // Only for what the item belongs to. A way back still needs the step it goes
  // back to in view.
  const referenceById = useMemo(
    () => indexItemsById([...(referenceItems ?? []), ...items]),
    [items, referenceItems]
  )
  const isMoment = item.kind === 'moment'

  // Two things have to be true before a field can be changed: the reader is
  // allowed to, and there is somewhere for the change to go.
  const isEditable = canEdit && onEdit !== undefined

  const dates = useMemo(
    () => buildItemDates({ formatTimeZone: formatters.timeZoneName, item }),
    [formatters, item]
  )
  const fields = useMemo(
    () => buildItemFields({ item, itemsById: referenceById, resolveDisplay }),
    [item, referenceById, resolveDisplay]
  )
  const labelSets = useMemo(
    () => buildItemLabelSets({ item, resolveDisplay }),
    [item, resolveDisplay]
  )
  const children = useMemo(
    () => (isMoment ? buildMomentContent(items, item.id) : []),
    [isMoment, item.id, items]
  )

  // A filter can take the last of a moment's content away while the panel is
  // open, and the tab it was on goes with it.
  const hasContentTab = children.length > 0
  const activeTab = tab === 'content' && hasContentTab ? 'content' : 'settings'

  const previousId = stepCycle(cycleIds, item.id, -1)
  const nextId = stepCycle(cycleIds, item.id, 1)

  // No host to ask is the same as an item with no picture: the panel draws no
  // room for one rather than waiting for an answer that is never coming.
  const imageUrl = resolveImageUrl ? resolveImageUrl(item) : null

  // The back control names the item's own day even when the panel was not
  // opened from anything — pressing it opens that day either way.
  const ownDay = parseCalendarDate(item.start, { field: 'start', id: item.id })
  const reachable = reachableBack(back, itemsById)
  const backStep: CalendarPanelStep | null =
    reachable ?? (ownDay === null ? null : { type: 'day', date: ownDay })

  // A way back names where it goes, so the control reads as the path out: the
  // day it came off, or the moment it was opened inside.
  const backLabel =
    backStep === null
      ? undefined
      : backStep.type === 'day'
        ? formatters.mediumDate(backStep.date)
        : itemsById.get(backStep.id)?.title

  // Only an item with a page of its own can be opened. Content whose moment
  // the reader cannot see has none, and neither has anything the host could
  // not build an address for.
  const canOpen = item.href !== undefined

  /**
   * Keyed by the item, because the browser holds the last picture's pixels
   * until the next file arrives, and those pixels belong to the item that was
   * on show before this one. The title sits right above it, so naming the
   * picture would only have a screenreader read the item out twice.
   */
  const picture = (source: string) => (
    <img
      key={item.id}
      alt=""
      className={styles.selectors.heroImage}
      src={source}
    />
  )

  /** The mark that leads a field's value, drawn from what the host can draw. */
  const fieldMark = (mark: CalendarFieldMark | undefined, value: string) => {
    switch (mark) {
      case 'account':
        return (
          <Avatar fullName={value} size={CalendarSizes.cardMomentIconSize} />
        )
      case 'channel':
        return renderChannelIcon?.(item, 'field')
      case 'moment':
      case 'story':
        return (
          <Icon
            ariaHidden={true}
            glyph={calendarKindGlyph(theme, mark)}
            size={CalendarSizes.cardMomentIconSize}
          />
        )
      case 'workflow':
        return renderStatusIcon?.(item, Size.Icon)
      default:
        return null
    }
  }

  /**
   * Every field is the block sidebar's own component. The ones a person can
   * change are drawn the same way and save themselves; the rest are there to
   * be read.
   */
  const readOnlyField = (
    definition: FieldDefinition,
    extras: { iconGlyph?: string; valueLeadingNode?: ReactNode } = {}
  ) => (
    <Field
      key={definition.id}
      {...extras}
      field={definition}
      readOnly={true}
      onChange={noop}
    />
  )

  const dateDefinition = (
    id: string,
    label: string,
    isoDate: string | undefined
  ): FieldDefinition => ({
    id,
    displayName: label,
    type: 'date',
    data:
      isoDate === undefined ? null : { id: null, value: { value: isoDate } },
  })

  const dateField = (id: string, label: string, isoDate: string | undefined) =>
    readOnlyField(dateDefinition(id, label, isoDate), {
      iconGlyph: 'gem-calendar',
    })

  /**
   * A date the item can be put on, bounded so the pair of them can never end up
   * the wrong way round: the picker simply does not offer the days that would.
   * Clearing is not offered either — every date the panel draws is one the item
   * has to have.
   */
  const editableDateField = (
    id: string,
    label: string,
    isoDate: string | undefined,
    { allowedDates, field }: EditableDateBounds
  ) => (
    <Field<DateField>
      key={id}
      dateOptions={{ allowedDates, enableDateRemoval: false }}
      field={dateDefinition(id, label, isoDate)}
      iconGlyph="gem-calendar"
      onChange={({ data }) => {
        const chosen = data?.value?.value
        if (chosen === undefined) return
        onEdit?.({
          date: formatISO(parseISO(chosen), { representation: 'date' }),
          field,
          item,
        })
      }}
    />
  )

  /** The day the item's parent moment goes up, when the month has loaded it. */
  const parentStart =
    item.parentId === undefined
      ? null
      : calendarDay(
          referenceById.get(item.parentId)?.start,
          'start',
          item.parentId
        )

  // A moment cannot start after it ends; content cannot go up before the moment
  // it belongs to does, which is the rule a drag onto an earlier day follows.
  const startsBefore = isMoment ? calendarDay(dates.end, 'end', item.id) : null
  const startsAfter = isMoment ? null : parentStart

  const startDateField = isEditable
    ? editableDateField('start-date', 'Start Date', dates.start, {
        allowedDates:
          startsBefore !== null
            ? { end: startsBefore }
            : startsAfter !== null
              ? { start: startsAfter }
              : undefined,
        field: 'startDate',
      })
    : dateField('start-date', 'Start Date', dates.start)

  // A moment ends where its own dates say; a piece of content ends on the day
  // it comes down off its channel. Neither can end before the day it starts.
  const ownStart = calendarDay(dates.start, 'start', item.id)
  const endDateField = isEditable
    ? editableDateField('end-date', 'End Date', dates.end, {
        allowedDates: ownStart === null ? undefined : { start: ownStart },
        field: 'endDate',
      })
    : dateField('end-date', 'End Date', dates.end)

  const selectField = (field: CalendarItemField) =>
    readOnlyField(
      {
        id: field.label.toLowerCase(),
        displayName: field.label,
        type: 'singleSelect',
        options: [{ label: field.value, position: 0, value: field.id }],
        deactivatedOptions: [],
        data: { id: null, value: { id: field.id } },
      },
      { valueLeadingNode: fieldMark(field.mark, field.value) }
    )

  const labelSetField = (set: CalendarItemLabelSet) =>
    readOnlyField({
      id: `label-set-${set.label}`,
      displayName: set.label,
      type: 'multiSelect',
      options: set.values.map((each, position) => ({
        label: each.name,
        position,
        value: each.id,
      })),
      deactivatedOptions: [],
      data: { id: null, value: { ids: set.values.map((each) => each.id) } },
      // The resolver names labels and does not colour them, so the tokens
      // are plain.
      isColorDisabled: true,
    })

  const settings = (
    <>
      <div className={styles.selectors.dates}>
        {startDateField}
        {endDateField}
      </div>

      {dates.time !== undefined &&
        readOnlyField({
          id: 'scheduled-time',
          displayName: 'Scheduled Time',
          type: 'textarea',
          data: { id: null, value: { value: dates.time } },
        })}

      {fields.map(selectField)}
      {labelSets.map(labelSetField)}
    </>
  )

  return (
    <div className={styles.selectors.panel}>
      <div className={styles.selectors.crumbs}>
        {backStep !== null && backLabel !== undefined && (
          <span className={styles.selectors.back}>
            <Button
              buttonText={backLabel}
              icon={<Icon ariaHidden={true} glyph="gem-chevron-w" />}
              iconPosition={ButtonIconPosition.leading}
              name="panel-back"
              size={ButtonSize.Small}
              variant="ghost"
              onClick={() => onBack(backStep)}
            />
          </span>
        )}
        <Button
          ariaLabel="Close"
          icon={<Icon ariaHidden={true} glyph="gem-close" />}
          name="close-item-panel"
          size={ButtonSize.Small}
          variant="ghost"
          onClick={onClose}
        />
      </div>

      <div
        className={classNames(styles.selectors.body, styles.selectors.itemBody)}
      >
        {/* The name stays a heading whether or not it can be changed: it is
            what the panel is about, and a control is no reason to stop saying
            so. The input inside takes its type from the heading. */}
        <h2 className={styles.selectors.itemTitle}>
          {isEditable ? (
            <ClickToEditTitleInput
              multiline={true}
              objectName="item"
              readOnly={false}
              title={item.title}
              onSubmit={(title) => onEdit({ field: 'title', item, title })}
            />
          ) : (
            item.title
          )}
        </h2>

        <div className={styles.selectors.cycle}>
          <span className={styles.selectors.navigation}>
            <Button
              ariaLabel="Previous item"
              disabled={previousId === null}
              icon={<Icon ariaHidden={true} glyph="gem-chevron-w" />}
              name="previous-item"
              size={ButtonSize.Small}
              variant="ghost"
              onClick={() => previousId && onStep(previousId)}
            />
            {canOpen && (
              <span className={styles.selectors.openPill}>
                <Button
                  buttonText="Open"
                  name="open-item"
                  size={ButtonSize.Small}
                  variant="ghost"
                  onClick={() => onNavigate(item)}
                />
              </span>
            )}
            <Button
              ariaLabel="Next item"
              disabled={nextId === null}
              icon={<Icon ariaHidden={true} glyph="gem-chevron-e" />}
              name="next-item"
              size={ButtonSize.Small}
              variant="ghost"
              onClick={() => nextId && onStep(nextId)}
            />
          </span>
          {item.isPrivate && (
            <span className={styles.selectors.privateLock}>
              <Icon
                ariaLabel="Private"
                glyph="gem-locked"
                size={CalendarSizes.panelLockSize}
              />
            </span>
          )}
        </div>

        {/* Nothing stands in for a picture nobody has given the item: the
            fields simply start higher. */}
        {imageUrl !== null && (
          <div
            className={classNames(styles.selectors.hero, {
              [styles.selectors.heroContent]: !isMoment,
              [styles.selectors.heroMoment]: isMoment,
            })}
          >
            {imageUrl === undefined ? (
              <DotLoader height={Spacing.XS * 12} />
            ) : canOpen ? (
              <button
                aria-label={`Open ${item.title}`}
                className={styles.selectors.heroButton}
                type="button"
                onClick={() => onNavigate(item)}
              >
                {picture(imageUrl)}
              </button>
            ) : (
              picture(imageUrl)
            )}
            {isMoment && (
              <span className={styles.selectors.momentBadge}>
                <Icon
                  ariaHidden={true}
                  glyph={calendarKindGlyph(theme, 'moment')}
                  size={Size.Icon}
                />
              </span>
            )}
          </div>
        )}

        {isMoment ? (
          <SidebarTabs.Root
            className={styles.selectors.tabs}
            value={activeTab}
            onValueChange={(value) =>
              onTabChange(value === 'content' ? 'content' : 'settings')
            }
          >
            {/* A moment with no content in view offers no tab for it: an empty
                list would only say what the missing tab already says. */}
            <SidebarTabs.List aria-label="Moment details">
              <SidebarTabs.Tab value="settings">Settings</SidebarTabs.Tab>
              {hasContentTab && (
                <SidebarTabs.Tab value="content">Content</SidebarTabs.Tab>
              )}
            </SidebarTabs.List>
            <SidebarTabs.Panel value="settings">{settings}</SidebarTabs.Panel>
            {hasContentTab && (
              <SidebarTabs.Panel
                className={styles.selectors.tabPanel}
                value="content"
              >
                {children.map((child) => (
                  <CalendarContentRow
                    key={child.id}
                    accountName={
                      child.accountId
                        ? resolveDisplay?.('account', child.accountId)
                        : undefined
                    }
                    color={resolveItemColor(
                      child,
                      itemsById,
                      resolveChosenColor
                    )}
                    item={child}
                    momentTitle={item.title}
                    renderChannelIcon={renderChannelIcon}
                    renderStatusIcon={renderStatusIcon}
                    onOpen={(each) => onOpenChild(each.id)}
                  />
                ))}
              </SidebarTabs.Panel>
            )}
          </SidebarTabs.Root>
        ) : (
          settings
        )}
      </div>
    </div>
  )
}
ItemPanel.displayName = 'Calendar: ItemPanel'
